import { action, observable } from 'mobx';
import { BaseEditViewStore } from 'core/stores';
import { ExistingPatientDataForm } from 'application/facility-order/forms';
import { LocalPatientService } from 'application/patient/services';
import { SubCategoryService } from 'application/product/services';
import { BaasicDropdownStore } from 'core/stores';
import { FacilityOrderTemplateService } from 'application/facility-order/services';
import { EligibilityStatusService } from 'application/eligibility-status/services';
import _ from 'lodash';
import { subCategoriesSort } from 'core/utils';

class ExistingPatientDataModalViewStore extends BaseEditViewStore {
    form = new ExistingPatientDataForm({
        onSuccess: async (form) => {
            await this.onSubmit(form.values());
        }
    });

	@observable accessor eligibilityStatus = null;
    constructor(rootStore, componentProps) {
        super(rootStore, {
            name: 'existing-patient-data-modal',
            Form: ExistingPatientDataForm,
            actions: {},
            FormClass: ExistingPatientDataForm
        });

        this.rootStore = rootStore;
        this.modalParams = componentProps.modalParams;

        this.subCategoryService = new SubCategoryService(this.rootStore.application.baasic.apiClient);
        this.localPatientService = new LocalPatientService(this.rootStore.application.baasic.apiClient);
        this.eligibilityStatusService = new EligibilityStatusService(rootStore.application.baasic.apiClient);

        this.facilityOrderId = this.modalParams.data.facilityOrderId;

        this.usIntakeStatus = this.modalParams.data.usIntakeStatus;
        if(!(this.usIntakeStatus && this.usIntakeStatus.completedOn)){
            this.form.fields.get('eligibilityStatusId').set('rules', null);	
        }

        this.rootStore.viewStore.setNavigationOptions({
            title: this.modalParams.data.titleData,
            hideColon: true
        });

        this.isEnteralWeek = this.modalParams.data.isEnteralWeek;
        this.isNewFlow = this.modalParams.data.isNewFlow;
        this.goBack = this.modalParams.data.goBack;
        this.newFlowStatusId = this.modalParams.data.newFlowStatusId;
        this.patients = this.modalParams.data.patients.map(p => {
            return { ...p };
        });

        this.subCategoryStore = new BaasicDropdownStore({}, {
            fetchFunc: async (term) => {
                const subCategorysAbrv = this.isEnteralWeek ? ["G-Tube", "Oral-Supplements", "Enteral"] : ["G-Tube", "Oral-Supplements", "Enteral", "Respiratory---trachs-supply", "Trach-Tube","Urologicals", "Ostomy-Products"];
                const response = await this.subCategoryService.find({ searchQuery: term, rpp: 100, sort: "itemCategory.name|asc", embed: "ItemCategory" });
                let data =  subCategoriesSort(response.data.item.map(i=> {return {...i, name: i.name}}));
                let filteredSubCategories = _.filter(data, i => {
                    return subCategorysAbrv.some(s => i.abrv === s);
                });
                return filteredSubCategories;
            },
        });

        this.eligibilityStatusStore = new BaasicDropdownStore({
            filterable: true,
        }, {
            fetchFunc: async (term) => {
                const response = await this.eligibilityStatusService.find({
                    searchQuery: term,
                    rpp: 50
                });
                return response.data.item;
            },
            onChange: () => {
                this.eligibilityStatus = this.eligibilityStatusStore.value && this.eligibilityStatusStore.value.name;
            }
        });

        this.onPatientDataChange = (item) => {
            this.form.set({ patientId: item.patientGeneralInfo.ptID });
        }
    }

    async createFacilityOrderTemplate(patientId) {
        const facilityOrderTemplateService = new FacilityOrderTemplateService(this.rootStore.application.baasic.apiClient);

        const { subCategoryId, eligibilityStatusId } = this.form.values();

        // try {
            await facilityOrderTemplateService.createBySubCategory(subCategoryId, this.facilityOrderId, patientId, eligibilityStatusId);
        // }
        // catch (err) {
        //     this.rootStore.notificationStore.error('Error occured while trying to create facility order template.', err);
        // }
    }

    async createLocalPatient() {
        const selectedPatientId = this.form.values().patientId;
        const selectedPatient = this.patients && this.patients.find(p => p.patientGeneralInfo.ptID === selectedPatientId);

        const payors = selectedPatient.patientInsuranceInfo.payors && selectedPatient.patientInsuranceInfo.payors.length > 0 && [...selectedPatient.patientInsuranceInfo.payors];
        const primaryInsurance = payors && payors.find(p => p.payorLevel === 'Primary');
        const secondaryInsurance = payors && payors.find(p => p.payorLevel === 'Secondary');
        const tertiaryInsurance = payors && payors.find(p => p.payorLevel === 'Tertiary');

        try {
            const newLocalPatient = {
                firstName: selectedPatient.patientGeneralInfo.name.first,
                lastName: selectedPatient.patientGeneralInfo.name.last,
                dateOfBirth: selectedPatient.patientGeneralInfo.birthDate,
                ssn: selectedPatient.patientGeneralInfo.ssn,
                height: selectedPatient.patientClinicalInfo.height,
                weight: selectedPatient.patientClinicalInfo.weight,
                patientId: selectedPatient.patientGeneralInfo.ptID,
                patientKey: selectedPatient.brightreeID,
                primaryInsurance: primaryInsurance && primaryInsurance.payorPolicyInfo.name,
                secondaryInsurance: secondaryInsurance && secondaryInsurance.payorPolicyInfo.name,
                tertiaryInsurance: tertiaryInsurance && tertiaryInsurance.payorPolicyInfo.name
            };

            const localPatientResponse = await this.localPatientService.create(newLocalPatient);
            return localPatientResponse.data;
        }
        catch (err) {
            this.rootStore.notificationStore.error('Error occured while trying to create local patient.', err);
        }
    }

    async checkForExistingLocalPatient(patientId) {
        try {
            const localPatientResponse = await this.localPatientService.find({ patientId });

            if (localPatientResponse.data.item && localPatientResponse.data.item.length > 0) {
                return localPatientResponse.data.item[0];
            }

            return null;
        }
        catch (err) {
            this.rootStore.notificationStore.error('Error occured while trying to get brightree patient data.', err);
        }
    }

    @action.bound
    async onSubmit(data) {
        const { patientId } = data;

        try {
            const existingLocalPatient = await this.checkForExistingLocalPatient(patientId);
            if (existingLocalPatient) {
                await this.createFacilityOrderTemplate(existingLocalPatient.id);
                if (this.isNewFlow) {
                    this.goBack();
                }
                else {
                    this.rootStore.routerStore.goTo('master.app.main.facility-order.facility-order-template.edit', { facilityOrderId: this.facilityOrderId, patientId: existingLocalPatient.id, patientKey: existingLocalPatient.patientKey });
                }
                this.rootStore.notificationStore.success('Successfully created template for existing patient.');
            }
            else {
                const localPatient = await this.createLocalPatient();
                await this.createFacilityOrderTemplate(localPatient.id);

                if (this.isNewFlow) {
                    this.goBack();
                }
                else {
                    this.rootStore.routerStore.goTo('master.app.main.facility-order.facility-order-template.list', { id: this.facilityOrderId });
                }

                await setTimeout(() => this.rootStore.notificationStore.success('Successfully created patient and template.'), 10);
            }
            this.modalParams.close();
        }
        catch (err) {
            this.rootStore.notificationStore.error('Error occured while trying to create patient and template.', err);
        }
    }
}

export default ExistingPatientDataModalViewStore;
